import React, { FC, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Grid, Button, Select, MenuItem, Autocomplete, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { headCells } from './head-cell';
import useStyles from './styles';
import { AppState } from '../../../store';
import SubscriptionConfig from '../../../services/subscription-config';
import { ShortMessagePostRequest } from '../../../entities/short-message-request-configuration-v1/models';
import { shortMessageRequestConfigAction } from '../../../store/commands/general-commands/actions';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import SubscriptionsCatalog from '../../../services/subscriptions-catalog';
import { sendSubscriptionToDevice } from '../../../store/commands/subscriptions/actions';
import ConfigurationFiles from '../../../services/config-files';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import { getCommandsByDeviceKey } from '../../../store/commands/actions';
import { setModalOpen } from '../../../store/modal/actions';
import { subscriptionChangeConfirmationMessage } from '../messages';
import SendFileToDeviceButton from '../send-file-to-device-button';
import SimpleTableCustomCell from '../../simple-table-custom-cell';
import { RetrieveConfigFilesLibrary } from '../../../entities/config-files-library-v1/retrieve-config-files-library';

type SelectOption = {
  id: string;
  label: string;
};

const Subscription: FC = () => {
  const [subscriptionOptions, setSubscriptionOptions] = useState<SelectOption[] | []>([]);
  const [subscriptionId, setSubscriptionId] = useState<number | undefined>();
  const [onboardSubscriptionId, setOnboardSubscriptionId] = useState<number | undefined>();
  const [subscriptionName, setSubscriptionName] = useState<string | undefined>();
  const [communicatorConfig, setCommunicatorConfig] = useState<string | undefined>();
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const deviceKey = selectedEntity?.deviceKey;
  const classes = useStyles();
  const dispatch = useDispatch();
  const clientAuth: any = client?.authenticate();
  const userName = clientAuth.loginId;
  const [tableData, setTableData] = useState<any[] | []>([]);
  const [currentSHA, setCurrentSHA] = useState<string>('');

  const handleOnDownloadClick = async () => {
    const configurationFiles = new ConfigurationFiles(
      client,
      envVariables.configurationFilesV1BaseUrl,
    );
    try {
      if (deviceKey) {
        const response = await configurationFiles.pushConfigFiles({
          configs: [
            {
              deviceKey,
              configFile: {
                fileTypeId: '10086',
                hash: currentSHA,
              },
            },
          ],
        });
        if (response.status === 200 || response.status === 201) {
          dispatch(
            enqueueSnackbarAction({
              message: 'Send file to device operation successful',
              options: {
                variant: 'success',
              },
            }),
          );
          dispatch(getCommandsByDeviceKey({ deviceKey, statuses: '1' }, true));
        } else {
          dispatch(
            enqueueSnackbarAction({
              message: 'Send file to device operation failed',
              options: { variant: 'error' },
            }),
          );
        }
      }
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Wakeup operation failed',
          options: { variant: 'error' },
        }),
      );
    }
  };

  const handleClickOpen = (row: any) => {
    setCurrentSHA(row.id);
    dispatch(
      setModalOpen({
        open: true,
        message: subscriptionChangeConfirmationMessage,
        accept: handleOnDownloadClick,
        cancel: () => { },
      }),
    );
  };

  const getCommunicatorConfigSubscriptions = useCallback(
    async (isButtonClicked?: boolean) => {
      if (deviceKey) {
        const subscriptionConfigService = new SubscriptionConfig(
          client,
          envVariables.subscriptionConfig,
        );
        const input = { deviceKey };
        try {
          const response = await subscriptionConfigService.getSubscriptions(input);
          const subscriptionConfig = response.data.subscriptionsConfig
            ? response.data.subscriptionsConfig[0]
            : undefined;
          if (subscriptionConfig) {
            setSubscriptionId(subscriptionConfig.subscriptionId);
            setSubscriptionName(subscriptionConfig.subscriptionName);
            setCommunicatorConfig(subscriptionConfig.currentCommunicatorConfig);
          }
          if (isButtonClicked) {
            const shortMessageInput: ShortMessagePostRequest = {
              configs: [{ deviceKey, fileTypeId: '10060' }],
              userId: userName,
            };
            dispatch(shortMessageRequestConfigAction(shortMessageInput, deviceKey));
          }
        } catch (error: any) {
          if (error.message.includes('404'))
            dispatch(
              enqueueSnackbarAction({
                message: 'Device is not associated to any subscription',
                options: { variant: 'warning', resumeHideDuration: 7000 },
              }),
            );
          else
            dispatch(
              enqueueSnackbarAction({
                message: `Fetching current subscription failed: ${error.toString()}`,
                options: { variant: 'error' },
              }),
            );
        }
      }
    },
    [client, deviceKey, dispatch, envVariables.subscriptionConfig, userName],
  );

  const getCommunicatorConfigScheduler = useCallback(
    async (isButtonClicked?: boolean) => {
      if (deviceKey) {
        try {
          if (isButtonClicked) {
            const shortMessageInput: ShortMessagePostRequest = {
              configs: [{ deviceKey, fileTypeId: "10064" }, { deviceKey, fileTypeId: "10086" }],
              userId: userName,
            };
            dispatch(shortMessageRequestConfigAction(shortMessageInput, deviceKey));
          }
        } catch (error: any) {
          if (error.message.includes('404'))
            dispatch(
              enqueueSnackbarAction({
                message: 'Device is not associated to any subscription',
                options: { variant: 'warning', resumeHideDuration: 7000 },
              }),
            );
          else
            dispatch(
              enqueueSnackbarAction({
                message: `Fetching current subscription failed: ${error.toString()}`,
                options: { variant: 'error' },
              }),
            );
        }
      }
    },
    [client, deviceKey, dispatch, envVariables.subscriptionConfig, userName],
  );

  const getSubscriptions = useCallback(async () => {
    const subscriptionsCatalog = new SubscriptionsCatalog(
      client,
      envVariables.subscriptionCatalogV1BaseUrl,
    );
    const response = await subscriptionsCatalog.getSubscriptions();
    const subscriptions = response.data.configs;
    if (subscriptions) {
      const subscriptionsResponse: SelectOption[] = subscriptions
        .sort((a, b) => {
          const v1 = a.subscriptionName || '';
          const v2 = b.subscriptionName || '';
          if (v1 < v2) {
            return -1;
          }
          if (v1 > v2) {
            return 1;
          }
          return 0;
        })
        .map((s) => ({
          id: `${s.subscriptionId}`,
          label: `[${s.subscriptionId}] - ${s.subscriptionName}`,
        }));
      setSubscriptionOptions(subscriptionsResponse);
    }
  }, [client, envVariables.subscriptionCatalogV1BaseUrl]);

  const getConfigurationFiles = useCallback(async () => {
    if (deviceKey) {
      const configFilesLibrary = new ConfigurationFilesLibrary(
        client,
        envVariables.configurationFilesLibraryV1BaseUrl,
      );
      const deviceType = selectedEntity?.deviceType;
      const fileTypeIds = ['10516'];
      if (deviceType !== 'PL444') {
        fileTypeIds.push('10086');
      }
      const responses = await Promise.all(
        fileTypeIds.map((fileTypeId) =>
          configFilesLibrary.getConfigFiles({ fileTypeId, isRetrievable: true }),
        ),
      );
      const configFilesLibraries = responses.reduce(
        (result: RetrieveConfigFilesLibrary[], response) => [
          ...result,
          ...(response?.data?.configs || []),
        ],
        [],
      );
      if (configFilesLibraries) setTableData(configFilesLibraries);
    }
  }, [client, deviceKey, envVariables.configurationFilesLibraryV1BaseUrl, selectedEntity]);
  useEffect(() => {
    getSubscriptions();
    getConfigurationFiles();
    getCommunicatorConfigSubscriptions();
  }, [deviceKey, getSubscriptions, getConfigurationFiles, getCommunicatorConfigSubscriptions]);

  const queueSubscription = async () => {
    if (deviceKey) {
      dispatch(sendSubscriptionToDevice(onboardSubscriptionId, deviceKey));
    }
  };

  const tableHeaderUnlock = [
    ...headCells,
    {
      id: 'sendFileToDevice',
      label: '',
      accessor: '',
      customCellComponent: SendFileToDeviceButton,
      callback: handleClickOpen,
    },
  ];

  return (
    <Grid container direction="row" id="grid-container">
      <Grid item xs={12}>
        <Grid container direction="row">
          <Grid item xs={6}>
            <Box px={1} pt={2} pb={1} display="flex" id="current-onboard-sub-id-label">
              <Typography variant="h6">Current Onboard Subscription ID: </Typography>
              <Box pl={2} id="current-onboard-sub-id">
                <Typography>
                  {subscriptionId ? `${subscriptionId} [ ${subscriptionName} ]` : ''}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box px={1} pt={2} pb={1} display="flex" id="current-communicator-config-label">
              <Typography variant="h6">Current Communicator Config: </Typography>
              <Box pl={2} id="current-communicator-config">
                <Typography>{communicatorConfig !== '' ? communicatorConfig : 'N/A'}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box px={1} pb={1}>
              <Button
                id="get-current-suscription"
                variant="outlined"
                onClick={() => getCommunicatorConfigSubscriptions(true)}
              >
                Get Current Subscription
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Box px={1} pt={2} pb={1} display="flex">
          <Typography variant="h6">Onboard Subscription ID: </Typography>
          <Box pl={2}>
            <Autocomplete
              id="onboard-sub-id-options"
              className={classes.select}
              options={subscriptionOptions && subscriptionOptions}
              renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
              onChange={(event, newValue) => {
                if (newValue) {
                  setOnboardSubscriptionId(Number(newValue.id));
                }
              }}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={4} />
      <Grid item xs={2}>
        <Box px={1} pb={1}>
          <Button
            id="send-sub-to-device-btn"
            variant="outlined"
            onClick={() => queueSubscription()}
            disabled={!onboardSubscriptionId}
          >
            Send Subscription To Device
          </Button>
        </Box>
      </Grid>
      <Grid container direction="row">
        <Grid item xs={10}>
          <Box px={1} pt={2} pb={1} display="flex">
            <Typography variant="h6">Communicator Configuration File</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Box px={1} pb={1}>
            <Button
              variant="outlined"
              className={classes.button}
              onClick={() => getCommunicatorConfigScheduler(true)}
            >
              Get Scheduler
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {tableData && (
          <Box mt={2} border={1} borderColor="grey.300">
            <SimpleTableCustomCell
              id="communicator-config-file-table"
              rows={tableData}
              initialSort="fileName"
              headCells={tableHeaderUnlock}
              maxHeight={300}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default Subscription;
